/**
 * The Stations screen (M6 plan P34, spec §8 "Station management"): the corporation's stations (built or building),
 * the charters waiting for a site, the cap, and the tariff and docking rights of every claim it holds. Reached from
 * the Corporation screen. Thin: the rules are `stationsModel.ts`.
 */
import type { ReactNode } from 'react'
import type { CommissionView, CorpView, DockingRightsName, PlayerStationView } from '../../strategic/types'
import { FocusButton } from '../focus'
import { formatCr, formatPct } from '../format'
import { chartersWaiting, constructionModel, dockingLabel, stationCount, stationCountLabel, stationEarnings, tariffLabel, typeLabel } from './stationsModel'

/** A held claim's settings while being edited. */
export interface ClaimDraft {
  tariff: number | null
  docking: DockingRightsName
}

export interface StationsProps {
  commission: CommissionView
  stations: readonly PlayerStationView[]
  claims: CorpView['claims']
  /** The claim drafts (keyed by system id). */
  drafts: ReadonlyMap<string, ClaimDraft>
  busy: boolean
  status: string | null
  systemName(id: string): string
  onBack(): void
  onCommission(): void
  onManage(id: string): void
  onTariff(systemId: string, delta: 1 | -1 | 0): void
  onDocking(systemId: string, delta: 1 | -1): void
  onApplyClaim(systemId: string): void
}

function stateText(s: PlayerStationView): string {
  const c = constructionModel(s)
  if (c === null) return `Level ${s.level}`
  const what = s.level === 0 ? `Building L${s.targetLevel}` : `Upgrading to L${s.targetLevel}`
  return `${what} · ${Math.round(c.supplied * 100)}% supplied · ${c.days}`
}

export function Stations(p: StationsProps): ReactNode {
  const v = p.commission
  let i = 0
  const waiting = chartersWaiting(v)
  return (
    <div className="sui fp-panel corp-panel" data-testid="stations-screen">
      <div className="corp-head">
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="stations-back" onActivate={p.onBack}>
          ← Corporation
        </FocusButton>
        <h3 className="corp-title">Stations</h3>
      </div>
      <p className="fp-meta" data-testid="stations-cap">
        {stationCountLabel(stationCount(v))} · {v.used} of {v.capacity} stations and charters · Treasury {formatCr(v.treasury)}
      </p>

      {p.stations.length === 0 ? (
        <p className="sui-empty" data-testid="stations-empty">
          No stations yet. Buy a charter for a system, then commission a station on one of its approved sites.
        </p>
      ) : (
        <ul className="corp-list" data-testid="stations-list">
          {p.stations.map((s) => (
            <li key={s.id} className="corp-row" data-testid={`station-row-${s.id}`}>
              <span>
                <strong>{s.name}</strong> · {typeLabel(s.type)} · {p.systemName(s.systemId)}
                <span className="fp-row__sub">
                  {' '}
                  {stateText(s)} · {stationEarnings(s)}
                  {s.manager !== null ? ` · managed by ${s.manager.name}` : ''}
                </span>
              </span>
              <FocusButton group="corp" index={i++} className="ui-btn--small" testId={`station-manage-${s.id}`} onActivate={() => p.onManage(s.id)}>
                Manage
              </FocusButton>
            </li>
          ))}
        </ul>
      )}

      {waiting.length > 0 ? (
        <p className="fp-meta" data-testid="stations-charters">
          Charters waiting: {waiting.map((s) => `${p.systemName(s.systemId)} ×${s.charters}`).join(', ')}
        </p>
      ) : null}
      <FocusButton group="corp" index={i++} className="ui-btn--small" testId="stations-commission" disabled={p.busy} onActivate={p.onCommission}>
        Charters and commissioning…
      </FocusButton>

      <h4 className="corp-sub">Claims: tariff and docking rights</h4>
      {p.claims.length === 0 ? (
        <p className="sui-empty" data-testid="stations-claims-empty">
          Hold a system&apos;s claim to set its tariff and docking rights.
        </p>
      ) : (
        <ul className="corp-list" data-testid="stations-claims">
          {p.claims.map((c) => {
            const d = p.drafts.get(c.systemId) ?? { tariff: null, docking: c.docking }
            return (
              <li key={c.systemId} className="corp-row" data-testid={`claim-row-${c.systemId}`}>
                <span>
                  {p.systemName(c.systemId)}
                  <span className="fp-row__sub"> now {formatPct(c.tariff)}</span>
                </span>
                <FocusButton
                  group="corp"
                  index={i++}
                  className="ui-btn--small"
                  testId={`claim-tariff-${c.systemId}`}
                  title="Left/right steps the tariff; press for automatic"
                  disabled={p.busy}
                  onActivate={() => p.onTariff(c.systemId, 0)}
                  onAdjust={(dd) => p.onTariff(c.systemId, dd)}
                >
                  Tariff ◂ {tariffLabel(d.tariff)} ▸
                </FocusButton>
                <FocusButton
                  group="corp"
                  index={i++}
                  className="ui-btn--small"
                  testId={`claim-docking-${c.systemId}`}
                  disabled={p.busy}
                  onActivate={() => p.onDocking(c.systemId, 1)}
                  onAdjust={(dd) => p.onDocking(c.systemId, dd)}
                >
                  Docking ◂ {dockingLabel(d.docking)} ▸
                </FocusButton>
                <FocusButton
                  group="corp"
                  index={i++}
                  className="ui-btn--small"
                  testId={`claim-apply-${c.systemId}`}
                  disabled={p.busy}
                  onActivate={() => p.onApplyClaim(c.systemId)}
                >
                  Apply
                </FocusButton>
              </li>
            )
          })}
        </ul>
      )}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="stations-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
